import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './index.css';
import App from './App';
import { PublicBrandingProvider } from './hooks/usePublicBranding';
import reportWebVitals from './reportWebVitals';

const googleClientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();

const resizeObserverMessagePattern = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)\.?/i;

function patchResizeObserverLoop() {
  if (typeof window === 'undefined' || typeof window.ResizeObserver !== 'function') {
    return;
  }

  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserverPatched extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }
  };
}

function installResizeObserverNoiseFilter() {
  const previousOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (resizeObserverMessagePattern.test(String(message || ''))) {
      return true;
    }
    if (typeof previousOnError === 'function') {
      return previousOnError(message, source, lineno, colno, error);
    }
    return false;
  };

  window.addEventListener(
    'error',
    (event) => {
      if (resizeObserverMessagePattern.test(String(event.message || ''))) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true,
  );

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      typeof reason === 'string'
        ? reason
        : reason && typeof reason.message === 'string'
          ? reason.message
          : '';
    if (resizeObserverMessagePattern.test(message)) {
      event.preventDefault();
    }
  });

  const originalConsoleError = console.error;
  console.error = (...args) => {
    const serialized = args
      .map((arg) => (typeof arg === 'string' ? arg : arg instanceof Error ? arg.message : ''))
      .join(' ');
    if (resizeObserverMessagePattern.test(serialized)) {
      return;
    }
    originalConsoleError(...args);
  };
}

function patchReactErrorOverlayHook() {
  if (typeof window === 'undefined') {
    return;
  }

  const applyPatch = () => {
    const hook = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__;
    if (!hook || typeof hook !== 'object') {
      return false;
    }

    const patchMethod = (methodName) => {
      const original = hook[methodName];
      if (typeof original !== 'function' || original.__resizeObserverPatched) {
        return;
      }
      const wrapped = (...args) => {
        const message = args
          .map((arg) => (arg && typeof arg.message === 'string' ? arg.message : typeof arg === 'string' ? arg : ''))
          .join(' ');
        if (resizeObserverMessagePattern.test(message)) {
          return;
        }
        return original(...args);
      };
      wrapped.__resizeObserverPatched = true;
      hook[methodName] = wrapped;
    };

    patchMethod('handleRuntimeError');
    patchMethod('showError');
    return true;
  };

  if (applyPatch()) {
    return;
  }

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    if (applyPatch() || attempts > 80) {
      window.clearInterval(timer);
    }
  }, 50);
}

patchResizeObserverLoop();
installResizeObserverNoiseFilter();
patchReactErrorOverlayHook();

const root = ReactDOM.createRoot(document.getElementById('root'));

const appTree = (
  <BrowserRouter>
    <PublicBrandingProvider>
      <App />
    </PublicBrandingProvider>
  </BrowserRouter>
);

root.render(
  googleClientId ? <GoogleOAuthProvider clientId={googleClientId}>{appTree}</GoogleOAuthProvider> : appTree,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
